import { __decorate } from "tslib";
import { attr, html, nullableNumberConverter, observable, slotted, volatile, when } from '@microsoft/fast-element';
import { FoundationElement } from '@microsoft/fast-foundation';
import { ChameleonElementMixin } from '../common/mixin';
import { DEV } from 'esm-env';
export const badgeCounterTemplate = html `<template role="status">
  <slot ${slotted('defaultSlot')} ?hidden=${(c) => c.shouldRenderMax}></slot>
  ${when((c) => c.shouldRenderMax, html `<slot name="max">99+</slot>`)}
</template>`;
export class BadgeCounterComponent extends ChameleonElementMixin(FoundationElement) {
    defaultSlotChanged(_, newValue) {
        if (this.isConnected) {
            if (newValue) {
                this.normalizeValue();
            }
        }
    }
    labelChanged() {
        this.setAccessibleLabel();
    }
    constructor() {
        super();
        this.size = 'medium';
        this.variant = 'primary';
        this.border = 'none';
        this.defaultSlot = [];
        this.handleContentMutation = (mutationsList) => {
            const hasMutationThatWeCareAbout = mutationsList.some((mutation) => mutation.type === 'childList' || mutation.type === 'characterData');
            if (hasMutationThatWeCareAbout) {
                this.normalizeValue();
                this.classList.toggle('dot', !this.textContent);
            }
        };
        this.contentObserver = new MutationObserver(this.handleContentMutation);
    }
    get shouldRenderMax() {
        return (this.value ?? 0) > 99;
    }
    connectedCallback() {
        super.connectedCallback();
        this.classList.toggle('dot', !this.textContent);
        this.setAccessibleLabel();
        this.contentObserver.observe(this, { childList: true, subtree: true, characterData: true });
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.contentObserver.disconnect();
    }
    normalizeValue() {
        const content = this.defaultSlot.map((node) => node.textContent).join('');
        const parsedValue = Number.parseInt(content);
        this.value = Number.isNaN(parsedValue) ? null : parsedValue;
    }
    setAccessibleLabel() {
        if (this.label) {
            this.setAttribute('aria-label', `${this.label}`);
            return;
        }
        if (DEV) {
            this.validateAccessibleLabel();
        }
    }
    validateAccessibleLabel() {
        if (!this.getAttribute('aria-label')?.length) {
            console.error('CHAMELEON-A11Y: BadgeCounter component must have an accessible label provided by a non-empty label attribute to be used as aria-label.');
        }
    }
}
__decorate([
    attr()
], BadgeCounterComponent.prototype, "size", void 0);
__decorate([
    attr()
], BadgeCounterComponent.prototype, "variant", void 0);
__decorate([
    attr()
], BadgeCounterComponent.prototype, "border", void 0);
__decorate([
    attr({ converter: nullableNumberConverter })
], BadgeCounterComponent.prototype, "value", void 0);
__decorate([
    attr()
], BadgeCounterComponent.prototype, "label", void 0);
__decorate([
    observable
], BadgeCounterComponent.prototype, "defaultSlot", void 0);
__decorate([
    volatile
], BadgeCounterComponent.prototype, "shouldRenderMax", null);
